// =============================================================================
// FarmSmartly — Application configuration constants
// Domain enums shared across pages. Not user data — these mirror the values
// enforced by CHECK constraints in the database schema.
// =============================================================================

// =============================================================================
// Feature flags — farm module availability
// Controlled by VITE_ENABLED_MODULES env var (comma-separated module names).
// Example: VITE_ENABLED_MODULES=POULTRY          → hides Dairy everywhere
//          VITE_ENABLED_MODULES=POULTRY,DAIRY     → both visible (default)
// Affects: registration page, sidebar, bottom nav, and route access guards.
// =============================================================================
const _rawModules = import.meta.env.VITE_ENABLED_MODULES || 'POULTRY,DAIRY';
export const ENABLED_MODULES = new Set(
  _rawModules.split(',').map((m) => m.trim().toUpperCase()).filter(Boolean)
);
/** Returns true if the given module type is enabled for this build. */
export const isModuleEnabled = (moduleType) =>
  ENABLED_MODULES.has(String(moduleType).toUpperCase());

export const CATEGORIES = [
  'Feed', 'Medicine', 'Labor', 'EB Bill', 'Water Bill',
  'Fuel', 'Transport', 'Equipment', 'Misc',
];

export const DAIRY_EXPENSE_CATEGORIES = ['Veterinary', 'Breeding', 'Milk Purchase', 'Shed'];

export const BIRD_TYPES = [
  {
    name: 'Chicken',     emoji: '🐔', incubationDays: 21,
    gradient: 'from-primary-600 to-primary-800', color: 'text-primary-700 dark:text-primary-400',
    varieties: ['Broiler', 'Village', 'Layer', 'Silkie', 'Aseel'],
  },
  {
    name: 'Duck',        emoji: '🦆', incubationDays: 28,
    gradient: 'from-blue-500 to-blue-700',       color: 'text-blue-700 dark:text-blue-400',
    varieties: ['Pekin', 'Muscovy', 'Khaki Campbell', 'Indian Runner'],
  },
  {
    name: 'Quail',       emoji: '🐦', incubationDays: 17,
    gradient: 'from-amber-500 to-amber-700',     color: 'text-amber-700 dark:text-amber-400',
    varieties: ['Coturnix / Japanese', 'Bobwhite'],
  },
  {
    name: 'Guinea Fowl', emoji: '🪶', incubationDays: 28,
    gradient: 'from-purple-500 to-purple-700',   color: 'text-purple-700 dark:text-purple-400',
    varieties: ['Helmeted', 'Crested'],
  },
  {
    name: 'Geese',       emoji: '🪿', incubationDays: 30,
    gradient: 'from-teal-500 to-teal-700',       color: 'text-teal-700 dark:text-teal-400',
    varieties: ['Embden', 'Toulouse', 'African', 'Chinese'],
  },
  {
    name: 'Turkey',      emoji: '🦃', incubationDays: 28,
    gradient: 'from-orange-500 to-orange-700',   color: 'text-orange-700 dark:text-orange-400',
    varieties: ['Broad Breasted White', 'Heritage', 'Bourbon Red'],
  },
];

export const getBirdType  = (name) => BIRD_TYPES.find((b) => b.name === name) || BIRD_TYPES[0];
export const getBirdEmoji = (name) => getBirdType(name).emoji;
export const getBirdVarieties = (name) => getBirdType(name).varieties ?? [];

/** Format date as MMMYY (e.g., "May3026") */
const formatStartDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear().toString().slice(-2);
  const day = date.getDate().toString().padStart(2, '0');
  return `${month}${day}${year}`;
};

/** Human-readable label for a flock: "Broiler Chicken May3026" or just "Duck Jun1526" */
export const flockLabel = (flock) => {
  const baseLabel = flock.variety ? `${flock.variety} ${flock.birdType}` : flock.birdType;
  const dateSuffix = flock.startDate ? ` ${formatStartDate(flock.startDate)}` : '';
  return `${baseLabel}${dateSuffix}`;
};

export const MORTALITY_REASONS = ['Disease', 'Predator', 'Accident', 'Injury', 'Unknown'];

export const INVENTORY_CATEGORIES = [
  { value: 'FEED',     label: 'Feed',     emoji: '🌾' },
  { value: 'MEDICINE', label: 'Medicine', emoji: '💊' },
  { value: 'OTHER',    label: 'Other',    emoji: '📦' },
];

export const DAIRY_INVENTORY_CATEGORIES = [
  { value: 'MILKING_EQUIPMENT', label: 'Milking Equipment', emoji: '🪣' },
  { value: 'DAIRY_SUPPLEMENT',  label: 'Dairy Supplement',  emoji: '🧴' },
];

export const INVENTORY_UNITS = ['kg', 'bags', 'liters', 'ml', 'vials', 'tablets', 'units'];

export const EXPIRY_WARNING_DAYS = { urgent: 15, warning: 30 };

export const FEED_TYPES = [
  'Starter Crumble',
  'Grower Pellet',
  'Layer Mash',
  'Finisher Pellet',
  'Scratch Grain',
  'Supplement Mix',
];

/** Today's date in YYYY-MM-DD format for form defaults (local date, no UTC shift) */
const _today = new Date();
export const TODAY = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, '0')}-${String(_today.getDate()).padStart(2, '0')}`;

// =============================================================================
// Bird Stage Configuration — mirrors backend BirdStage enum
// =============================================================================

export const BIRD_STAGES = [
  {
    code: 'CHICK',
    emoji: '🐣',
    gradient: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
  },
  {
    code: 'GROWER',
    emoji: '🐥',
    gradient: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  {
    code: 'LAYER',
    emoji: '🐔',
    gradient: 'from-purple-400 to-pink-500',
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800',
  },
];

/** Get bird stage configuration by code (CHICK, GROWER, LAYER) */
export const getBirdStageConfig = (code) => BIRD_STAGES.find((s) => s.code === code) || null;
